/**
 * The one number `@tanstack/react-virtual` cannot be told at build time.
 *
 * `--row-h` is declared in `rem` on purpose, so a user at XXXL gets taller rows
 * — but a virtualiser needs a NUMBER OF PIXELS, and it needs the same number
 * every time it asks or the scroll position drifts under its own feet.
 *
 * So the resolved value is read ONCE from the live document with
 * `getComputedStyle` and treated as constant until something that can change it
 * actually changes: density, font scale, or a font finishing loading (a fallback
 * metric and the real one differ, and rows sized against the fallback are wrong
 * from the moment the webfont lands). `colour.md` names exactly those triggers;
 * this subscribes to each rather than polling or measuring per row.
 */

/** The three triggers, plus the initial read. */
export function readRowHeight(element: HTMLElement = document.documentElement): number {
  const raw = getComputedStyle(element).getPropertyValue('--row-h').trim()
  const pixels = remToPixels(raw)
  // A DEFAULT THAT IS NOT ZERO. In jsdom, and in the frame before the
  // stylesheet has applied, `--row-h` reads as ''. Zero would make the
  // virtualiser render every row in the archive at once; 36 is cozy, the
  // default density, so being wrong here is a slightly wrong estimate rather
  // than an unbounded one.
  return Number.isFinite(pixels) && pixels > 0 ? pixels : 36
}

function remToPixels(value: string): number {
  if (value.endsWith('rem')) {
    const root = Number.parseFloat(getComputedStyle(document.documentElement).fontSize)
    return Number.parseFloat(value) * (Number.isFinite(root) && root > 0 ? root : 16)
  }
  if (value.endsWith('px')) return Number.parseFloat(value)
  return Number.NaN
}

/**
 * Call `onChange` whenever the resolved row height can have changed.
 *
 * Returns an unsubscribe. The density and scale attributes live on `<html>`, so
 * one MutationObserver on its attributes covers both; `document.fonts.ready`
 * covers the webfont swap.
 */
export function watchRowHeight(onChange: (height: number) => void): () => void {
  let last = readRowHeight()
  const check = (): void => {
    const next = readRowHeight()
    if (next !== last) {
      last = next
      onChange(next)
    }
  }

  const observer = new MutationObserver(check)
  observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-density', 'data-fs'] })
  // `fonts` is absent in jsdom and in older Safari; the row height is simply
  // never re-read there, which is the pre-webfont behaviour and not a failure.
  void document.fonts?.ready.then(check).catch(() => undefined)

  return () => observer.disconnect()
}
